import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Send, MapPin, Mail, Phone } from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ContactHero() {
  const { t } = useLanguage();
  return (
    <section data-testid="contact-hero" className="bg-[#C8C5BC]/30 pt-28 sm:pt-48 pb-16 sm:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-base sm:text-lg tracking-[0.2em] uppercase font-semibold text-[#C4883A] mb-4 animate-fade-in-up opacity-0">{t.contact.label}</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#1C2333] max-w-3xl mx-auto leading-tight animate-fade-in-up opacity-0 animate-delay-100">
          {t.contact.heroTitle1}<br /><span className="font-medium text-[#1B3D2F]">{t.contact.heroTitle2}</span>
        </h1>
        <p className="mt-8 text-[#1C2333]/70 text-lg sm:text-xl lg:text-2xl max-w-2xl mx-auto leading-relaxed animate-fade-in-up opacity-0 animate-delay-200">{t.contact.heroBody}</p>
      </div>
    </section>
  );
}

function ContactForm() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent successfully!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C2333] mb-8">{t.contact.formTitle}</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <Label htmlFor="name" className="text-base font-medium text-[#1C2333]">{t.contact.fullName}</Label>
            <Input id="name" name="name" value={form.name} onChange={handleChange} required placeholder={t.contact.namePlaceholder} data-testid="contact-name-input" className="h-12 rounded-none border-[#C8C5BC] focus:border-[#C4883A] focus:ring-[#C4883A]" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="email" className="text-base font-medium text-[#1C2333]">{t.contact.emailAddress}</Label>
            <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required placeholder={t.contact.emailPlaceholder} data-testid="contact-email-input" className="h-12 rounded-none border-[#C8C5BC] focus:border-[#C4883A] focus:ring-[#C4883A]" />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="subject" className="text-base font-medium text-[#1C2333]">{t.contact.subject}</Label>
          <Input id="subject" name="subject" value={form.subject} onChange={handleChange} required placeholder={t.contact.subjectPlaceholder} data-testid="contact-subject-input" className="h-12 rounded-none border-[#C8C5BC] focus:border-[#C4883A] focus:ring-[#C4883A]" />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="message" className="text-base font-medium text-[#1C2333]">{t.contact.message}</Label>
          <Textarea id="message" name="message" value={form.message} onChange={handleChange} required placeholder={t.contact.messagePlaceholder} rows={6} data-testid="contact-message-input" className="rounded-none border-[#C8C5BC] focus:border-[#C4883A] focus:ring-[#C4883A] resize-none" />
        </div>
        <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="bg-[#C4883A] text-white hover:bg-[#A8722E] px-10 py-5 text-lg sm:text-xl font-medium transition-all duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed self-start">
          {sending ? t.contact.sending : t.contact.sendBtn} <Send size={16} />
        </button>
      </form>
    </div>
  );
}

function ContactInfo() {
  const { t } = useLanguage();
  const details = [
    { id: "address", icon: MapPin, label: t.contact.address, value: t.contact.addressValue },
    { id: "email", icon: Mail, label: t.contact.email, value: t.contact.emailValue },
    { id: "phone", icon: Phone, label: t.contact.phone, value: t.contact.phoneValue },
  ];

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C2333] mb-8">{t.contact.findUs}</h2>
        <div data-testid="google-map" className="w-full h-72 sm:h-80 bg-[#C8C5BC]/20 overflow-hidden">
          <iframe title="Abule Imole Location" src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d15000!2d3.9172!3d7.7136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sng!4v1700000000000" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      </div>
      <div className="flex flex-col gap-5">
        {details.map((d) => (
          <div key={d.id} className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#A3B18A]/20 text-[#1B3D2F] shrink-0"><d.icon size={18} /></div>
            <div>
              <h4 className="font-['Cormorant_Garamond'] text-base font-semibold text-[#1C2333] mb-1">{d.label}</h4>
              <p className="text-base text-[#1C2333]/70">{d.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <ContactHero />
      <section data-testid="contact-form-section" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <ContactForm />
            <ContactInfo />
          </div>
        </div>
      </section>
    </div>
  );
}
